import { Injectable, signal } from '@angular/core';
import { SecurityService } from '../security.service';
import { DbDaoService } from '../../db/db-dao.service';
import { UuidService } from '../../utils/uuid.utils';
import { SyncService } from '../sync.service';
import { Invoice } from '@shared/classes/invoice.class';
import { Image } from '@shared/classes/image.class';
import { Customer } from '@shared/classes/customer.class';
import { InvoiceEntry, SpecialTaxing } from '@shared/classes/invoice-entry.class';
import { PRODUCT_TYPES } from '@shared/constants/product-types';
import { UPDATES_TYPES } from '@shared/constants/updates-types.const';
import { DISCOUNTS_TYPES } from '@shared/constants/discounts-types.const';
import { TranslateService } from '@ngx-translate/core';
import { AlertService } from '../alert.service';
import { InvoicesService } from '../invoices.service';
import { InvoicePayment } from '@shared/classes/invoice-payment.class';
import { GastronomyTable } from '@shared/classes/gastronomy-table.class';
import { InvoiceDiscountModal } from '@features/collection-view/invoice-discount-modal/invoice-discount-modal.component';
import { ModalService } from '../modal.service';
import { LoadingService } from '../loading.service';
import { defer, firstValueFrom, Subject } from 'rxjs';
import { auditTime } from 'rxjs/operators';
import { retryWithBackoff } from '../../utils/retry-with-backoff';
import { Company } from '@shared/classes/company.class';
import { LogService } from '../logger/log.service';
import { ReferenceEntity } from '@shared/classes/reference-entity';
import { UserNotificationService } from '../user-notification/user-notification.service';
import moment from 'moment';
import { GiftCard } from '@features/gift-card/gift-card';
import { PAYMENT_TRANSACTION } from '@shared/constants/payment-providers.const';
import { InvoicesApiService } from '../../api/invoices-api.service';
import { InvoiceDetailsSwipeEvent } from '@features/invoice-details/interfaces/invoice-details.events';
import { SwipeDirection } from '@shared/constants/swipe-direction.enum';
import { MultipleGuestsService } from '../multiple-guests/multiple-guests.service';
import { CustomerService } from '../customers.service';
import { ignoreImageProps } from '@core/services/system/logger';
import { EmployeesFacadeStore } from '@state/employees';
import { Employee } from '@shared/classes/employee.class';
import { PaymentMethod } from '@shared/classes/payment-method.class';
import { InvoiceForLogsClass } from '@shared/classes/invoice/invoice-for-logs.class';
import { DepositService } from '@core/services/system/deposit/deposit.service';
import { GiftCardService } from '@features/gift-card';
import { trustValue } from '@core/services';
import { SalesSubChannelTypesEnum, SelfOrderInvoiceFulfillmentTypesEnum } from '@shared/constants';
import { SelfOrdersService } from '@core/services/system/self-order-invoices/self-orders.service';
import { PaymentResult } from '@shared/classes/payment-result.class';
import { AdyenLastTransactionMapService } from '@core/services/system/adyen';

@Injectable()
export class CartService {
  private activeInvoice: Invoice = null;
  private separetedInvoice: Invoice = null;
  private invoiceForSeparation: Invoice = null;
  private readonly _activeInvoiceUpdated = signal<Invoice>(null, { equal: () => false });
  readonly activeInvoiceUpdated = this._activeInvoiceUpdated.asReadonly();

  private readonly _cartComponentUpdated = signal(0);
  readonly cartComponentUpdated = this._cartComponentUpdated.asReadonly();


  private readonly _invoiceCustomerChanged = new Subject<Customer>();
  readonly invoiceCustomerChanged = this._invoiceCustomerChanged.asObservable();

  private readonly _invoiceEntryChange = signal<any>(null, { equal: () => false });

  private readonly _invoiceForSeparationChange = signal<any>(null, { equal: () => false });

  private readonly _invoiceTableChange = signal<any>(null, { equal: () => false });

  private readonly _activePaymentMethod = signal<PaymentMethod>(null, { equal: () => false });

  private readonly _tableChanged = signal<any>(null, { equal: () => false });
  readonly tableChangedEvent = this._tableChanged.asReadonly();
  private logger = this.logService.createLogger('CartService');

  // Coalesce the expensive persist (DB write + sync) of rapid tap-adds. The cart
  // (activeInvoice) is the single source of truth — updated synchronously per tap;
  // only saveInvoice() is debounced. auditTime (not debounceTime) so it does NOT
  // starve under continuous tapping — fires at most once per window. `pendingSave`
  // guards against redundant/double saves (clean cart flush, flush + trailing audit).
  private readonly SAVE_COALESCE_MS = 400;
  private readonly saveTrigger$ = new Subject<void>();
  private pendingSave = false;

  constructor(
    public UuidService: UuidService,
    public SecurityService: SecurityService,
    public DbDaoService: DbDaoService,
    public SyncService: SyncService,
    public AlertService: AlertService,
    public TranslateService: TranslateService,
    public InvoicesService: InvoicesService,
    public LoadingService: LoadingService,
    public modalService: ModalService,
    public userNotificationService: UserNotificationService,
    public employeeFacadeStore: EmployeesFacadeStore,
    private invoicesApiService: InvoicesApiService,
    private multipleGuestsService: MultipleGuestsService,
    private customerService: CustomerService,
    private logService: LogService,
    private GiftCardService: GiftCardService,
    private depositService: DepositService,
    private selfOrderService: SelfOrdersService,
    private adyenLostTransactionService: AdyenLastTransactionMapService
  ) {
    // CartService is an app-lifetime singleton, so this subscription needs no teardown.
    this.saveTrigger$.pipe(auditTime(this.SAVE_COALESCE_MS)).subscribe(() => {
      if (this.pendingSave) this.saveInvoice();
    });
  }

  /** Schedule a coalesced persist of the current cart. Safe to call on every tap. */
  public requestSave(): void {
    this.pendingSave = true;
    this.saveTrigger$.next();
  }

  /**
   * Persist immediately if there are unsaved changes (boundaries: before payment,
   * app pause, leaving the screen). No-op on a clean cart, so it is safe to call freely.
   */
  public flushSave(): void {
    if (this.pendingSave) this.saveInvoice();
  }

  public set activePaymentMethodEvent(paymentMethod: PaymentMethod) {
    this._activePaymentMethod.set(paymentMethod);
  }

  readonly activePaymentMethod = this._activePaymentMethod.asReadonly();

  public get isGastroModeEnabled(): boolean {
    return this.SecurityService.loggedCompanyData.getValue()?.isRestaurantEnabled;
  }

  public get isActiveInvoiceWithSelfOrderTakeAway(): boolean {
    const activeInvoice: Invoice = this.getActiveInvoice();
    return (
      Boolean(activeInvoice) &&
      activeInvoice.isSelfOrder &&
      Boolean(activeInvoice.selfOrder) &&
      Boolean(activeInvoice.selfOrder.fulfillmentOptions === SelfOrderInvoiceFulfillmentTypesEnum.TAKE_AWAY)
    );
  }

  public createInvoice(setAsActive: boolean = true): Invoice {
    const invoice = this.InvoicesService.createInvoice();
    if (setAsActive) {
      this.activeInvoice = invoice;
      this._activeInvoiceUpdated.set(this.activeInvoice);
    }
    return invoice;
  }

  public setActiveInvoice(invoice: Invoice, saveRequired: boolean = false): void {
    if (invoice) {
      this.activeInvoice = invoice;
      if (this.activeInvoice.customer) {
        this.DbDaoService.getDataByUUID(UPDATES_TYPES.Customer.type, this.activeInvoice.customer['uuid'])
          .then((data) => (data['data'] ? this.setCustomer(new Customer(data['data']), false) : this.setCustomer(null, false)))
          .catch(() => this.setCustomer(null, false));
      } else {
        this.recalculateEntriesPositions();
        this.calculateTotalCount(saveRequired);
      }
    }
  }

  public getActiveInvoice() {
    return this.activeInvoice;
  }

  public setSeparetedInvoice(invoice: Invoice) {
    this.separetedInvoice = invoice;
    if (invoice) {
      this.logger.info('Separeted invoice has been set', { invoiceUuid: invoice?.uuid });
    }
  }

  public getSeparetedInvoice(): Invoice {
    const { separetedInvoice } = this;
    if (separetedInvoice) {
      separetedInvoice.isSeparated = false;
    }
    return separetedInvoice;
  }

  public hasSeparetedInvoice(): boolean {
    if (this.separetedInvoice) {
      return this.separetedInvoice.invoiceEntries.length > 0;
    }
    return false;
  }

  public async setCustomerFromGuestToInvoiceCustomer(invoice: Invoice) {
    const invoiceEntryGuest = invoice.getGuestInvoiceEntries().find((_) => true);
    let customer: Customer = null;
    if (invoiceEntryGuest?.customerIdentifier) {
      customer = await this.customerService.getCustomerByUuid(invoiceEntryGuest.customerIdentifier);
    }
    this.setCustomerDataToInvoice(invoice, customer);
    this.InvoicesService.calculateInvoiceAmountAfterDiscount(invoice);
    return invoice;
  }

  public setInvoiceStoreAndEmployee(storeUuid: string, employeeUuid: string) {
    if (
      this.activeInvoice &&
      (!this.activeInvoice.store ||
        !this.activeInvoice.employee ||
        this.activeInvoice.store?.uuid !== storeUuid ||
        this.activeInvoice.employee?.uuid !== employeeUuid)
    ) {
      const store = new ReferenceEntity(storeUuid);
      this.activeInvoice.store = store;
      this.activeInvoice.employee = new ReferenceEntity(employeeUuid);
      this.activeInvoice.inventoryStore = store;
      this.logger.info('Active invoice store, employee, inventoryStore were set', { storeUuid, employeeUuid, inventoryStore: storeUuid });
      this.saveInvoice();
    }
  }

  public async addEntry(data: any, type: string, quantity: number, saveRequired: boolean = true) {
    if (this.adyenLostTransactionService.hasUncompletedTransactionInInvoice(this.activeInvoice?.uuid)) {
      this.adyenLostTransactionService.showMessageForUncompletedTransaction();
      return;
    }

    const { forceGiftCardPhysicalCode } = this.SecurityService.getLoggedCompanyData();
    if (forceGiftCardPhysicalCode && this.GiftCardService.isGiftCartEntryWithGiftCardCode(type, data)) {
      try {

        const existingGiftCarts: string[] = (this.activeInvoice?.getGiftCardsEntries() ?? [])
          .map((entry: InvoiceEntry) => entry.giftCardPhysicalCode)
          .filter(Boolean);
        const giftCardPhysicalCode = await this.GiftCardService.getGiftCardCodeForNewGiftCard(existingGiftCarts);
        if (!giftCardPhysicalCode) return;

        if (this.activeInvoice?.getGiftCardsEntries().some((entry: InvoiceEntry) => entry.giftCardPhysicalCode === giftCardPhysicalCode)) {
          this.userNotificationService.showMessage('giftcard_already_exist_msg', 'giftcard_already_exist_title');
          return;
        }
        data.giftCardPhysicalCode = giftCardPhysicalCode;
      } catch (err) {
        this.logger.error('GiftCard code fetch failed', err);
        return;
      }
    }

    if (!this.activeInvoice) this.createInvoice();
    this.logger.debug('Start to add entry to invoice', { invoiceUuid: this.activeInvoice?.uuid });
    let currentCartEntryIndex;
    let currentEntry: InvoiceEntry = null;

    this.addActiveEmployeeToSelfOrderInvocie();

    this.setEntryDiscount(data, data.discount, data.discountPercentage);
    InvoiceEntry.calculateEntryDiscountedPrice(data);

    data.guestNumber = data.guestNumber || this.multipleGuestsService.activeGuestNumber;

    currentCartEntryIndex = this.InvoicesService.getEntryByUuidAndCategory(PRODUCT_TYPES[type], data, this.activeInvoice?.invoiceEntries);

    if (currentCartEntryIndex.length === 0) {
      const limitInvoiceEntries = 255;
      if (this.activeInvoice?.invoiceEntries?.length > limitInvoiceEntries) {
        this.userNotificationService.showMessage('globl_error_invoice_entries_limit');
        return;
      }

      if (Boolean(data?.assignedId) && this.isGastroModeEnabled) {
        const uuidDeposit: string = this.UuidService.generate();
        const uuidProductWithDeposit: string = this.UuidService.generate();
        data.uuidDeposit = uuidDeposit;
        data.uuidProductWithDeposit = uuidProductWithDeposit;
      }

      this.addInvoiceEntryToActiveInvoice(data, PRODUCT_TYPES[type], quantity);

      if (Boolean(data?.assignedId) && this.isGastroModeEnabled) {
        try {
          const dataDeposit = await firstValueFrom(
            this.depositService.addDepositByProductUuid(data.assignedId, data.uuidProductWithDeposit, data.uuidDeposit)
          );
          if (dataDeposit) {
            dataDeposit.guestNumber = data.guestNumber;
            this.addInvoiceEntryToActiveInvoice(dataDeposit, PRODUCT_TYPES.DEPOSIT, quantity);
          }
        } catch (e) {
          this.logger.error(e, 'addEntry:addDepositByProductUuid');
        }
      }
    } else {
      currentEntry = this.activeInvoice?.invoiceEntries?.[currentCartEntryIndex[0]];

      const hasLinkedDeposit: boolean = Boolean(currentEntry?.assignedUuid);
      const localModificationDate: string = moment.utc().toISOString();

      if (currentEntry?.type !== PRODUCT_TYPES.DEPOSIT) {
        currentEntry.update(data);
        currentEntry.quantity += quantity;
        currentEntry.quantityForKitchenReceipt += quantity;

        this.setEntryDiscount(currentEntry, data.discount, data.discountPercentage);
        currentEntry && currentEntry.calculateDiscountedPrice();

        currentEntry.productVariant = {
          uuid: data.variant ? data.variant.uuid : data?.productVariant?.['uuid'],
        };
      }

      if (hasLinkedDeposit) {
        const currentDepositEntryIndex: number = this.activeInvoice?.invoiceEntries?.findIndex(
          (item: InvoiceEntry): boolean => item?.uuid === currentEntry.assignedUuid
        );
        const currentDepositEntry: InvoiceEntry = this.activeInvoice?.invoiceEntries?.[currentDepositEntryIndex];

        if (currentDepositEntry) {
          currentDepositEntry.quantity = currentEntry.quantity;
          currentDepositEntry.localModificationDate = localModificationDate;
        }
      }

      if (currentEntry?.type === PRODUCT_TYPES.DEPOSIT_RETURN) {
        const suffixNameDeposit: string = this.TranslateService.instant('invoice-entry.deposit-return.note');
        currentEntry.name = `${data.name} (${suffixNameDeposit})`;
      }

      currentEntry.localModificationDate = localModificationDate;

      this.logger.info('addEntry: currentEntry was updated with quantity in invoice', {
        currentEntryUuid: currentEntry?.uuid,
        quantity: currentEntry?.quantity,
        invoiceUuid: this.activeInvoice?.uuid,
      });
    }
    this.calculateTotalCount(saveRequired);

    // Remove if advanced TakeAway VAT rates functional needed
    if (this.SecurityService.getLoggedCompanyData()['isTakeAwayEnabled']) {
      let foundTakeAway = false;
      this.activeInvoice?.invoiceEntries?.forEach((entry) => {
        if (entry && entry.specialTaxing === SpecialTaxing.takeAway) foundTakeAway = true;
      });
      if (foundTakeAway) this.recalculateVatRates(SpecialTaxing.takeAway);
    }
  }

  public changeEntryQuantity(entry: InvoiceEntry, changeValue: number): void {
    if (this.adyenLostTransactionService.hasUncompletedTransactionInInvoice(this.activeInvoice?.uuid)) {
      this.adyenLostTransactionService.showMessageForUncompletedTransaction();
      return;
    }

    const activeInvoiceEntry: InvoiceEntry = this.getEntryFromActiveInvoice(entry?.uuid);

    const hasDeposit: boolean = Boolean(activeInvoiceEntry?.assignedUuid);

    const newEntry: InvoiceEntry = activeInvoiceEntry || entry;
    if (this.isEmployeeForbiddenToEditItems(newEntry, entry?.price)) {
      this.userNotificationService.showMessage('employee_are_not_able_to_edit_items');
      return;
    }
    newEntry.update(entry);
    if (!this.isEmployeeAllowedToRemoveItems(entry.quantityForKitchenReceipt, changeValue)) {
      this.userNotificationService.showEmployeeDoesntAllowedPopup();
      return;
    }
    if (newEntry?.type !== PRODUCT_TYPES.TIPS && this.isForbiddenToDeleteEntry(newEntry?.quantity, changeValue)) {
      this.userNotificationService.showInvoiceContainsPaymentAlert();
      return;
    }
    newEntry.quantity += changeValue;
    const quantityForKitchenReceipt = newEntry.quantityForKitchenReceipt + changeValue;
    newEntry.quantityForKitchenReceipt = quantityForKitchenReceipt < 0 ? 0 : quantityForKitchenReceipt;
    if (newEntry?.quantity === 0) {
      newEntry.deleted = true;
    }
    if (newEntry) {
      newEntry.calculateDiscountedPrice();
    }

    if (hasDeposit) {
      this.changeQuantityDeposit(activeInvoiceEntry.assignedUuid, newEntry.quantity);
    }

    this.calculateTotalCount(false);
    this.checkActiveInvoiceIsPaid();
    this.saveInvoice();
    this.recalculateEntriesPositions();
  }

  public changeEntries(entries: InvoiceEntry[]) {
    if (this.adyenLostTransactionService.hasUncompletedTransactionInInvoice(this.activeInvoice?.uuid)) {
      this.adyenLostTransactionService.showMessageForUncompletedTransaction();
      return;
    }

    entries.forEach((entry) => {
      const newEntry = this.getEntryFromActiveInvoice(entry?.uuid) || entry;
      newEntry.update(entry);
      if (entry.deleted) {
        newEntry.markAsDeleted();
      }
      if (newEntry) {
        newEntry.calculateDiscountedPrice();
      }
    });

    this.calculateTotalCount(false);
    this.checkActiveInvoiceIsPaid();
    this.recalculateEntriesPositions();
    this.saveInvoice();
  }

  private changeQuantityDeposit(uuid: string, quantity: number): void {
    const depositEntry: InvoiceEntry = this.getEntryFromActiveInvoice(uuid);

    if (!depositEntry) {
      return;
    }

    const newEntry: InvoiceEntry = depositEntry;

    newEntry.update(depositEntry);
    newEntry.quantity = quantity;

    if (newEntry?.quantity <= 0) {
      newEntry.deleted = true;
    }
  }

  private checkActiveInvoiceIsPaid() {
    if (this.activeInvoice) {
      const invoicePaymentsTotal = this.activeInvoice.getPaymentsTotal();
      if (this.activeInvoice?.amount > 0 && invoicePaymentsTotal >= this.activeInvoice?.amount) {
        this.activeInvoice.setInvoiceAsPaid();
        this.activeInvoice.invoiceId = this.InvoicesService.getInvoiceId();
      }
    }
  }

  private isEmployeeAllowedToRemoveItems(quantityForKitchenReceipt: number, changeValue: number): boolean {
    if (changeValue > 0) {
      return true;
    }
    const currentCompany: Company = this.SecurityService.getLoggedCompanyData();
    const currentEmployee: Employee = this.employeeFacadeStore.activeEmployeeSnapshot;
    if (currentCompany?.isRestaurantEnabled && !currentEmployee?.hasCancellationPermission) {
      const quantityForDeletion = quantityForKitchenReceipt - Math.abs(changeValue);
      return quantityForDeletion >= 0;
    }
    return true;
  }

  private isEmployeeForbiddenToEditItems(invoiceEntry: InvoiceEntry, changePrice: number): boolean {
    const { quantityForKitchenReceipt, quantity, price } = invoiceEntry;
    const currentCompany: Company = this.SecurityService.getLoggedCompanyData();
    const currentEmployee: Employee = this.employeeFacadeStore.activeEmployeeSnapshot;

    if (currentCompany?.isRestaurantEnabled && !currentEmployee?.hasCancellationPermission) {
      const quantityForPrinting = quantityForKitchenReceipt - Math.abs(quantity);
      return price !== changePrice && quantityForPrinting < 0;
    }
    return false;
  }

  private isForbiddenToDeleteEntry(quantity: number, changeValue: number) {
    const newQuantity = quantity + changeValue;
    const hasPayments = this.activeInvoice.isInvoiceHasPayments();
    const activeInvoiceEntries = this.activeInvoice.getActiveInvoiceEntries();
    const isLastActiveInvoiceEntry = activeInvoiceEntries.length === 1;
    return isLastActiveInvoiceEntry && hasPayments && newQuantity === 0;
  }

  // TODO: Investigate this method
  public getEntryFromActiveInvoice(entryUuid: string): InvoiceEntry {
    if (!this.activeInvoice) {
      return null;
    }
    const invoiceEntry = this.activeInvoice?.invoiceEntries?.find((invoiceEntry) => invoiceEntry?.uuid === entryUuid);
    return invoiceEntry || null;
  }

  private addInvoiceEntryToActiveInvoice(data: any, entryType: string, quantity: number) {
    // if product NOT exists in cart
    // push product to cart
    // set product quantity to 1 if it not already have quantity field

    let taxRate = 0;
    if (data.taxRate) {
      taxRate = data.taxRate;
    } else if (data.variant && data.variant.vatRate?.value) {
      taxRate = data.variant.vatRate.value;
    }
    const entryToAdd: InvoiceEntry = new InvoiceEntry({
      type: entryType,
      name: data?.name,
      shortName: data.shortName,
      image: data.image ? new Image(data.image) : null,
      note: data.note,
      quantity,
      quantityForKitchenReceipt: quantity || 1,
      price: data?.price,
      wasPrice: data.wasPrice,
      taxRate,
      position: this.activeInvoice?.invoiceEntries?.length || 0,
      discountedPrice: data.discountedPrice,
      discount: data.discount,
      discountPercentage: data.discountPercentage,
      bgColor: data.bgColor,
      guestNumber: data.guestNumber,
      employee: {
        uuid: this.employeeFacadeStore.activeEmployeeSnapshot?.uuid,
      },
      uuid:
        entryType === PRODUCT_TYPES.DEPOSIT && data['uuidDeposit']
          ? data['uuidDeposit']
          : entryType === PRODUCT_TYPES.PRODUCT && data['uuidProductWithDeposit']
            ? data['uuidProductWithDeposit']
            : this.UuidService.generate(),
    });
    entryToAdd.setLocalCreationDate();

    if (entryType !== PRODUCT_TYPES.DEPOSIT && data['uuidDeposit']) {
      entryToAdd.assignedUuid = data['uuidDeposit'];
    }

    if (entryType === PRODUCT_TYPES.DEPOSIT && data['uuidProductWithDeposit']) {
      entryToAdd.assignedUuid = data['uuidProductWithDeposit'];
      entryToAdd.quantityForKitchenReceipt = 0;
    }

    if (entryType === PRODUCT_TYPES.DEPOSIT || entryType === PRODUCT_TYPES.DEPOSIT_RETURN) {
      entryToAdd.quantityForKitchenReceipt = 0;
    }

    if (entryType === PRODUCT_TYPES.DEPOSIT_RETURN) {
      const suffixNameDeposit: string = this.TranslateService.instant('invoice-entry.deposit-return.note');
      entryToAdd.name = `${data.name} (${suffixNameDeposit})`;
    }

    entryToAdd.store = {
      uuid: this.SecurityService.getActiveStore()?.uuid,
    };

    const productTypes = [
      PRODUCT_TYPES.PRODUCT,
      PRODUCT_TYPES.GIFT_CARD,
      PRODUCT_TYPES.SERVICE,
      PRODUCT_TYPES.DEPOSIT,
      PRODUCT_TYPES.DEPOSIT_RETURN,
    ];
    if (productTypes.includes(entryType)) {
      entryToAdd.productVariant = {
        uuid: data.variant ? data.variant.uuid : data?.productVariant?.['uuid'],
      };

      if (data.productCategory) {
        entryToAdd.productCategory = {
          uuid: data.productCategory,
        };
      } else {
        entryToAdd.productCategory = null;
      }

      entryToAdd.inventoryStore = {
        uuid: this.SecurityService.getActiveStore()?.uuid,
      };

      entryToAdd.giftCardPhysicalCode = data.giftCardPhysicalCode;
    }

    this.logger.info('addInvoiceEntryToActiveInvoice: invoiceEntry was added with quantity to invoice', {
      invoiceEntryUuid: entryToAdd?.uuid,
      invoiceUuid: this.activeInvoice?.uuid,
      entryType: entryToAdd?.type,
      productName: entryToAdd.isGuest ? null : (data.productName ?? entryToAdd.name),
      productUuid: data.productUuid ?? data.variant?.productUuid ?? null,
      productVariantUuid: entryToAdd.productVariant?.uuid ?? null,
      guestNumber: entryToAdd.guestNumber,
    });
    this.activeInvoice?.invoiceEntries?.push(entryToAdd);
  }

  public getEntryImage(product, variant): Image {
    if (variant?.images?.length > 0 && variant?.images[0]?.document) {
      return new Image(variant.images[0].document);
    } else if (product?.images.length > 0 && product?.images[0]?.image) {
      return new Image(product.images[0].image);
    } else {
      return null;
    }
  }

  public setEntryDiscount(entry, discount, discountPercentage) {
    if (discount) {
      entry.discount = discount;
      entry.discountPercentage = 0;
    } else if (discountPercentage) {
      entry.discount = 0;
      entry.discountPercentage = discountPercentage;
    } else {
      entry.discount = 0;
      entry.discountPercentage = 0;
    }
  }

  public recalculateEntriesPositions() {
    let position = 0;
    const invoiceEntries = this.activeInvoice?.invoiceEntries
      ?.sort((a, b) => (a.isGuest === b.isGuest ? 0 : a.isGuest ? -1 : 1))
      .sort((a, b) => a.guestNumber - b.guestNumber);

    for (let i = 0; i < invoiceEntries.length; i++) {
      const current = invoiceEntries[i];
      if (current.deleted) {
        current.position = -1;
      } else {
        current.position = position;
        position++;
      }
    }
  }

  public calculateTotalCount(saveRequired: boolean = true) {
    this.activeInvoice && this.InvoicesService.calculateInvoiceAmountAfterDiscount(this.activeInvoice);

    if (saveRequired) {
      this.saveInvoice();
    } else {
      this._activeInvoiceUpdated.set(this.activeInvoice);
    }
  }

  public getCustomer() {
    return this.activeInvoice?.customer ?? null;
  }

  public setCustomer(customerData: Customer, saveRequired: boolean = true) {
    if (!this.activeInvoice) this.createInvoice();
    if (this.activeInvoice.isPaid && !customerData) return;
    this.activeInvoice = this.setCustomerDataToInvoice(this.activeInvoice, customerData);
    if (this.activeInvoice) this.calculateTotalCount(saveRequired);

    this.addActiveEmployeeToSelfOrderInvocie();

    this._invoiceCustomerChanged.next(customerData);
  }

  public getActiveTableName() {
    return this.activeInvoice?.gastronomyTableName ?? null;
  }

  public getActiveTableUuid() {
    return this.activeInvoice?.gastronomyTable?.uuid ?? null;
  }

  public getInvoiceTableChangeEvent() {
    return this._invoiceTableChange.asReadonly();
  }

  public async setTable(table: GastronomyTable) {
    if (!this.activeInvoice) this.createInvoice();
    this.activeInvoice.gastronomyTable = { uuid: table?.uuid };
    this.activeInvoice.gastronomyTableName = table?.name;

    if (this.activeInvoice?.isSelfOrder && !!this.activeInvoice?.selfOrder) {
      const { uuid = null, name = null } = table ?? {};
      this.activeInvoice.selfOrder = {
        ...this.activeInvoice.selfOrder,
        table: await this.selfOrderService.updateTableSelfOrder(uuid, name),
      };
    }

    this._invoiceTableChange.set(this.activeInvoice);
    this.saveInvoice();
    this.calculateGastronomyTableInvoices(this.activeInvoice).catch((err) =>
      this.logger.error(err, 'setTable:calculateGastronomyTableInvoices')
    );
  }

  public setCustomerDataToInvoice(invoice: Invoice, customerData: Customer): Invoice {
    if (customerData) {
      invoice.customer = {
        uuid: customerData?.uuid,
        dataToShowInList: customerData.dataToShowInList || this.TranslateService.instant('customers_unknown'),
        deleted: customerData?.deleted,
      };
      invoice.billingAddress = customerData.address;
      if (invoice.isWebshop || invoice.isSelfOrder) {
        return invoice;
      }
      if (!invoice.isPaid) {
        if (customerData.discount) {
          invoice.customerDiscount = customerData.discount;
          invoice.customerDiscountPercentage = 0;
        } else if (customerData.discountPercentage) {
          invoice.customerDiscount = 0;
          invoice.customerDiscountPercentage = customerData.discountPercentage;
        } else {
          invoice.customerDiscount = 0;
          invoice.customerDiscountPercentage = 0;
        }
      }
    } else if (invoice) {
      invoice.unlinkCustomerFromInvoice();
    }
    return invoice;
  }

  public setActiveInvoiceId(saveInvoice: boolean = true) {
    if (!this.activeInvoice) {
      this.createInvoice();
      saveInvoice = true;
    }
    if (this.activeInvoice && !this.activeInvoice.invoiceId) {
      this.activeInvoice.invoiceId = this.InvoicesService.getInvoiceId();
      this.logger.info('Invoice id was set to active invoice', {
        invoiceId: this.activeInvoice.invoiceId,
        invoiceUuid: this.activeInvoice?.uuid,
      });
      saveInvoice && this.saveInvoice();
    }
  }

  async addPaymentToInvoice(
    activeInvoice: Invoice,
    method: string,
    amount: any,
    amountGiven: any,
    giftCard?: GiftCard,
    result?: PaymentResult
  ): Promise<Invoice> {
    this.logger.debug('Start to add payment to invoice', { method, invoiceUuid: activeInvoice.uuid });
    activeInvoice = new Invoice(activeInvoice);

    const invoice =
      activeInvoice.hasGiftCardsEntries() || giftCard
        ? await this.addGiftCardPayment(activeInvoice, method, amount, amountGiven, giftCard, result)
        : await this.addPayment(activeInvoice, method, amount, amountGiven, result);

    const separatedInvoice = this.getSeparetedInvoice();
    if (separatedInvoice) {
      this.logger.info('addPaymentToInvoice: Save separeted invoice', { invoiceUuid: separatedInvoice.uuid });
      this.InvoicesService.saveInvoice(separatedInvoice);
    }
    this.logger.info('The payment was added to invoice', { method, invoiceUuid: invoice.uuid });
    return invoice;
  }

  private addGiftCardPayment(
    activeInvoice: Invoice,
    method: string,
    amount: any,
    amountGiven: any,
    giftCard: GiftCard,
    result?: PaymentResult
  ): Promise<Invoice> {
    let isPaidGiftCardInvoice = false;
    return this.InvoicesService.addPaymentToInvoice(
      activeInvoice,
      method,
      amount,
      amountGiven,
      PAYMENT_TRANSACTION.PAYMENT_PURCHASE,
      giftCard,
      result
    )
      .then((invoice: Invoice) => {
        const paidElsewhere: Invoice = this.appendPaymentsToInvoicePaidElsewhere(invoice);
        if (paidElsewhere) {
          return this.updateInvoiceOnServer(paidElsewhere);
        }
        if (invoice.isPaid) {
          isPaidGiftCardInvoice = true;
          invoice.invoiceId = this.InvoicesService.getInvoiceId();
          this.updateDiscountByTotalDiscountAmount(invoice);
        }
        return this.updateInvoiceOnServer(invoice);
      })
      .then((invoiceWithPayment: Invoice) => {
        this.activeInvoice = invoiceWithPayment;
        this._activeInvoiceUpdated.set(this.activeInvoice);
        this.logger.info('Payment method has been added to invoice with amount and amountGiven', {
          method,
          invoiceUuid: invoiceWithPayment.uuid,
          amount,
          amountGiven,
        });
        if (this.activeInvoice.isPaid) {
          this.logger.info('Active invoice has been paid', { invoiceUuid: this.activeInvoice.uuid });
          this.saveInvoice();
          this.calculateGastronomyTableInvoices(this.activeInvoice).catch((err) =>
            this.logger.error(err, 'addPaymentToInvoice:calculateGastronomyTableInvoices')
          );
        }
        return this.activeInvoice;
      })
      .catch((err) => {
        if (isPaidGiftCardInvoice) {
          this.logger.info('The paid invoice with gift card payment failed to sync, the invoice number should be decreased', {
            invoiceUuid: this.activeInvoice.uuid,
          });
          this.SecurityService.decreaseNextInvoiceNumber();
          this.userNotificationService.showGiftCardProcessingErrorMessage();
        }
        throw err;
      });
  }

  private addPayment(activeInvoice: Invoice, method: string, amount: any, amountGiven: any, result?: PaymentResult): Promise<Invoice> {
    return this.InvoicesService.addPaymentToInvoice(
      activeInvoice,
      method,
      amount,
      amountGiven,
      PAYMENT_TRANSACTION.PAYMENT_PURCHASE,
      null,
      result
    ).then((invoiceWithPayment: Invoice) => {
      invoiceWithPayment = this.appendPaymentsToInvoicePaidElsewhere(invoiceWithPayment) ?? invoiceWithPayment;
      this.activeInvoice = invoiceWithPayment;
      this._activeInvoiceUpdated.set(this.activeInvoice);
      this.logger.info('Payment with amount and amountGiven has been added to invoice', {
        method,
        amount,
        amountGiven,
        invoiceUuid: invoiceWithPayment.uuid,
      });
      if (this.activeInvoice.isPaid) {
        this.logger.info('Active invoice has been paid', { invoiceUuid: this.activeInvoice.uuid });
        this.updateDiscountByTotalDiscountAmount(this.activeInvoice);
        this.setActiveInvoiceId(false);
        this.calculateGastronomyTableInvoices(this.activeInvoice).catch((err) =>
          this.logger.error(err, 'addPaymentToInvoice:calculateGastronomyTableInvoices')
        );
      } else {
        this.saveInvoice();
      }
      return this.activeInvoice;
    });
  }

  // A sync during the terminal wait set the invoice as paid with a payment unknown here: keep its payments and number, add ours.
  private appendPaymentsToInvoicePaidElsewhere(invoice: Invoice): Invoice | null {
    const paidElsewhere: Invoice = this.activeInvoice;
    const ownUuids: string[] = invoice.invoicePayments.map(({ uuid }) => uuid);
    const otherUuids: string[] = paidElsewhere?.invoicePayments.map(({ uuid }) => uuid) ?? [];
    if (!paidElsewhere?.isPaid || paidElsewhere.uuid !== invoice.uuid || otherUuids.every((uuid) => ownUuids.includes(uuid))) {
      return null;
    }
    const newPayments = invoice.invoicePayments.filter(({ uuid }) => !otherUuids.includes(uuid));
    paidElsewhere.invoicePayments.push(...newPayments);
    this.logger.info('Invoice was paid on another POS during payment, payment appended', {
      invoiceUuid: invoice.uuid,
      newPayments: newPayments.length,
    });
    return paidElsewhere;
  }

  private updateDiscountByTotalDiscountAmount(activeInvoice: Invoice) {
    this.InvoicesService.calculateInvoiceAmountAfterDiscount(activeInvoice);
    const { discount, totalInvoiceDiscountAmount, customerDiscount, totalCustomerDiscountAmount } = activeInvoice;
    const shouldUpdateInvoiceDiscount = activeInvoice.hasDiscount() && discount > totalInvoiceDiscountAmount;
    if (shouldUpdateInvoiceDiscount) {
      activeInvoice.discount = totalInvoiceDiscountAmount;
      this.InvoicesService.calculateInvoiceAmountAfterDiscount(activeInvoice);
    }

    const shoudlUpdateCustomerDiscount = activeInvoice.hasCustomerDiscount() && customerDiscount > totalCustomerDiscountAmount;
    if (shoudlUpdateCustomerDiscount) {
      activeInvoice.customerDiscount = totalCustomerDiscountAmount;
      this.InvoicesService.calculateInvoiceAmountAfterDiscount(activeInvoice);
    }
  }

  private updateInvoiceOnServer(invoice: Invoice): Promise<Invoice> {
    // defer: each retry must send a new PUT; from(promise) only replays the first rejection.
    return firstValueFrom(
      defer(() => this.invoicesApiService.updateInvoice(invoice)).pipe(
        retryWithBackoff({ maxRetries: 3, initialDelay: 500, backoffMultiplier: 1 })
      )
    );
  }

  public setPrintedStatusToInvoice(invoice: Invoice): void {
    // TODO NEED TO REMOVE IN VERSION 2.10.0
    // this.logger.debug('Start to set printer status to invoice', { invoiceUuid: invoice.uuid });
    if (this.activeInvoice.uuid === invoice.uuid) {
      this.activeInvoice.isPrinted = true;
      this.setActiveInvoice(this.activeInvoice);
      this.saveInvoice();
    } else {
      invoice.isPrinted = true;
      this.InvoicesService.saveInvoice(invoice).catch((err) => this.logger.error(err, 'setPrintedStatusToInvoice:saveInvoice'));
    }
    this.logger.info('isPrinted status was added to invoice', { invoiceUuid: invoice.uuid });
  }

  public setShoppingStatusToInvoice(invoice: Invoice, shoppingStatus: string) {
    if (this.activeInvoice.uuid === invoice.uuid) {
      this.activeInvoice.fulfillmentState = shoppingStatus;
      this.setActiveInvoice(this.activeInvoice);
      this.saveInvoice();
      return;
    }
    invoice.fulfillmentState = shoppingStatus;
    this.InvoicesService.saveInvoice(invoice).catch((err) => this.logger.error(err, 'setPrintedStatusToInvoice:saveInvoice'));
    this.logger.info('The shoping status was added to invoice', { shoppingStatus, invoiceUuid: invoice.uuid });
  }

  private readonly _paymentChanged = signal<any>(null, { equal: () => false });
  readonly paymentChanged = this._paymentChanged.asReadonly();
  public paymentsMethodWatcher = () => this._paymentChanged.asReadonly();
  public paymentsMethodSetter = (payment) => this._paymentChanged.set(payment);

  public setPaymentsToActiveInvoice(payments: Array<InvoicePayment>) {
    this.activeInvoice.invoicePayments = payments;
    this._activeInvoiceUpdated.set(this.activeInvoice);
    this.saveInvoice();
  }

  public saveInvoice() {
    // Any persist (immediate or coalesced) satisfies the pending save request.
    this.pendingSave = false;

    if (
      (this.activeInvoice?.invoiceEntries?.length === 0 || this.activeInvoice.totalProductsCount === 0) &&
      (this.activeInvoice.discount || this.activeInvoice.discountPercentage)
    ) {
      this.removeInvoiceDiscount();
      return false;
    }

    this.logger.debug('saveInvoice', { activeInvoice: new InvoiceForLogsClass(ignoreImageProps(this.activeInvoice) as Invoice) });
    // TODO check for inconsistent state
    if (this.isActiveInvoiceDummy()) {
      let invoiceToDelete = this.activeInvoice;
      this.InvoicesService.deleteInvoice(invoiceToDelete)
        .then(() => {
          this.createInvoice();
        })
        .catch((err) => this.logger.error(err, 'saveInvoice:isActiveInvoiceDummy:deleteInvoice'));
      return false;
    }

    this.InvoicesService.saveInvoice(this.activeInvoice)
      .then((data) => {
        this._activeInvoiceUpdated.set(this.activeInvoice);
      })
      .catch((err) => {
        // Persist failed: re-arm so a later flush / next debounce retries (never lose the add).
        this.pendingSave = true;
        this.logger.error(err, 'saveInvoice:InvoicesService:saveInvoice');
      });
  }

  public setDataAfterSuccessPay() {
    const separatedInvoice = this.getSeparetedInvoice();
    if (separatedInvoice) {
      this.setActiveInvoice(separatedInvoice);
      this.saveInvoice();
      this.setSeparetedInvoice(null);
    } else {
      this.createInvoice();
    }
  }

  public cleanInvoiceEntriesKitchenQuantity(guestNumber: number): void {
    this.activeInvoice.cleanInvoiceEntriesKitchenQuantity(guestNumber);
    this.setActiveInvoice(this.activeInvoice);
    this.saveInvoice();
  }

  public cleanInvoiceEntriesKitchenQuantityForSelfOrderPaid(guestNumber: number, invoice: Invoice): void {
    invoice.cleanInvoiceEntriesKitchenQuantity(guestNumber);
    this.InvoicesService.saveInvoice(invoice).catch((err) => this.logger.error(err, 'saveInvoice:InvoicesService:saveInvoice'));
  }

  public async setNewTableToInvoice(table, invoice: Invoice): Promise<void> {
    if (table) {
      invoice.gastronomyTable ? (invoice.gastronomyTable.uuid = table.uuid) : (invoice.gastronomyTable = { uuid: table.uuid });
      invoice.gastronomyTableName = table.name;
    } else {
      invoice.gastronomyTable = null;
      invoice.gastronomyTableName = null;
    }

    if (invoice?.isSelfOrder && !!invoice?.selfOrder) {
      const { uuid = null, name = null } = table ?? {};
      invoice.selfOrder = {
        ...invoice.selfOrder,
        table: await this.selfOrderService.updateTableSelfOrder(uuid, name),
      };
    }

    this.InvoicesService.saveInvoice(invoice, false)
      .then(() => {
        invoice.uuid === this.activeInvoice.uuid && this.setActiveInvoice(invoice);
        this.calculateGastronomyTableInvoices(invoice)
          .then(() => {
            if (table) {
              this._tableChanged.set({ table: table.uuid, invoice: invoice.uuid });
            } else {
              this._tableChanged.set({ table: null, invoice: invoice.uuid });
            }
          })
          .catch((err) => this.logger.error(err, 'setNewTableToInvoice:saveInvoice:calculateGastronomyTableInvoices'));
      })
      .catch((err) => this.logger.error(err, 'setNewTableToInvoice:saveInvoice'));
  }

  public emitInvoiceUpdate = (invoice) => this._activeInvoiceUpdated.set(invoice);

  public notifyCartComponentUpdated(): void {
    this._cartComponentUpdated.update((n) => n + 1);
  }

  public isActiveInvoiceDummy(): boolean {
    const hasGuestInvoiceEntry = this.activeInvoice.hasGuestInvoiceEntry();
    if (
      (this.activeInvoice?.invoiceEntries?.length === 0 || this.activeInvoice.totalProductsCount === 0) &&
      !hasGuestInvoiceEntry &&
      !this.activeInvoice.invoiceId &&
      this.activeInvoice?.isDraft &&
      !this.activeInvoice.customer &&
      !this.activeInvoice.gastronomyTable &&
      !this.activeInvoice.discountCode &&
      !this.activeInvoice.isInvoiceHasPayments()
    ) {
      return true;
    }

    return false;
  }

  public setInvoiceDiscount(discountAmount: number, discountType: string) {
    if (this.adyenLostTransactionService.hasUncompletedTransactionInInvoice(this.activeInvoice?.uuid)) {
      this.adyenLostTransactionService.showMessageForUncompletedTransaction();
      return;
    }

    if (discountAmount === 0) {
      this.removeInvoiceDiscount();
      return false;
    }

    if (discountType === DISCOUNTS_TYPES.PERCENTAGE) {
      this.activeInvoice.discount = 0;
      this.activeInvoice.discountPercentage = discountAmount;
    } else {
      this.activeInvoice.discount = discountAmount;
      this.activeInvoice.discountPercentage = 0;
    }

    this.calculateTotalCount();
  }

  public removeInvoiceDiscount() {
    if (this.adyenLostTransactionService.hasUncompletedTransactionInInvoice(this.activeInvoice.uuid)) {
      this.adyenLostTransactionService.showMessageForUncompletedTransaction();
      return;
    }

    this.activeInvoice.discount = 0;
    this.activeInvoice.discountPercentage = 0;
    this.calculateTotalCount();
  }

  public async calculateGastronomyTableInvoices(invoice: Invoice | any): Promise<boolean> {
    try {
      const hallsData: any = await this.DbDaoService.getAllData(UPDATES_TYPES.GastronomyHall.type);
      let needToSaveDatabase = false;
      for (const hall of hallsData.data) {
        for (const table of hall.gastronomyTables) {
          if (invoice.gastronomyTable && table.uuid === invoice.gastronomyTable.uuid && !invoice.isPaid && !invoice.deleted) {
            const found = table.invoices.some((invUuid) => invUuid.uuid === invoice.uuid);
            if (!found) {
              table.invoices.push({ uuid: invoice.uuid });
              this.DbDaoService.upsertDataToCollection(UPDATES_TYPES.GastronomyHall.type, [hall]).catch((err) =>
                this.logger.error(err, 'calculateGastronomyTableInvoices:notfound:upsertDataToCollection')
              );
              needToSaveDatabase = true;
            }
          } else {
            const index = table.invoices.findIndex((invUuid) => invoice.uuid === invUuid.uuid);
            if (index !== -1) {
              table.invoices.splice(index, 1);
              needToSaveDatabase = true;
              this.DbDaoService.upsertDataToCollection(UPDATES_TYPES.GastronomyHall.type, [hall]).catch((err) =>
                this.logger.error(err, 'calculateGastronomyTableInvoices:found:upsertDataToCollection')
              );
            }
          }
        }
      }
      return needToSaveDatabase;
    } catch (err) {
      this.logger.error(err, 'calculateGastronomyTableInvoices:getAllDataFromCollection');
      return false;
    }
  }

  public getInvoiceForSeparation(): Invoice {
    return this.invoiceForSeparation;
  }

  public setInvoiceForSeparation(invoice: Invoice, clean?: boolean) {
    this.invoiceForSeparation = invoice;
    if (clean) this.invoiceForSeparation = null;
    this._invoiceForSeparationChange.set(invoice);
  }

  public getInvoiceEntryChangeEvent() {
    return this._invoiceEntryChange.asReadonly();
  }

  public getInvoiceForSeparationChangeEvent() {
    return this._invoiceForSeparationChange.asReadonly();
  }

  public async openInvoiceDiscountModal(activeInvoice: Invoice): Promise<any> {
    if (!activeInvoice || (activeInvoice && !activeInvoice.totalProductsCount)) return false;
    let invoiceDiscountData: any = {
      discount: activeInvoice.discount,
      discountPercentage: activeInvoice.discountPercentage,
    };
    const invoiceDiscountModal = await this.modalService.presentModal(InvoiceDiscountModal, { invoiceDiscountData: invoiceDiscountData });
    invoiceDiscountModal.onDidDismiss().then((data) => {
      if (data.data && data.data.closedByUser && data.data.discountType) {
        this.setInvoiceDiscount(data.data.discountDecimal, data.data.discountType);
      } else if (data.data && data.data.closedByUser && !data.data.discountType) {
        this.removeInvoiceDiscount();
      }
    });
    invoiceDiscountModal.present();
    return invoiceDiscountModal;
  }

  public async recalculateVatRates(param) {
    const activeInvoice = this.activeInvoice;

    if (param === SpecialTaxing.inHouse) {
      await Promise.all(
        activeInvoice.invoiceEntries.map(async (entry) => {
          entry.specialTaxing = SpecialTaxing.inHouse;
          if (entry.productVariant) {
            const data = await this.DbDaoService.getDataByUUID(UPDATES_TYPES.ProductVariant.type, entry.productVariant['uuid']);
            const rate = data.data.vatRate.value;
            if (rate) {
              entry.taxRate = rate;
              entry.totalTaxAmount = (entry.totalAmount / (1 + entry.taxRate)) * entry.taxRate;
            }
          }
        })
      );
    }

    if (param === SpecialTaxing.takeAway) {
      const currentCompany: Company = this.SecurityService.getLoggedCompanyData();
      if (currentCompany.defaultTakeAwayCompanyVATRate) {
        try {
          const takeAwayDefaultVat = await this.DbDaoService.getDataByUUID(
            UPDATES_TYPES.VATRate.type,
            currentCompany.defaultTakeAwayCompanyVATRate['uuid']
          );
          if (takeAwayDefaultVat?.data && trustValue(takeAwayDefaultVat.data.value)) {
            const takeAwayVAT = takeAwayDefaultVat.data.value;
            await Promise.all(
              activeInvoice.invoiceEntries.map(async (entry) => {
                if (entry.productVariant) {
                  try {
                    const data = await this.DbDaoService.getDataByUUID(UPDATES_TYPES.ProductVariant.type, entry.productVariant['uuid']);
                    if (data.data?.isTakeAwayAllowed) {
                      entry.taxRate = takeAwayVAT;
                      entry.specialTaxing = SpecialTaxing.takeAway;
                      entry.totalTaxAmount = (entry.totalAmount / (1 + entry.taxRate)) * entry.taxRate;
                    }
                  } catch (err) {
                    this.logger.error(err, 'recalculateVatRates:getDataByUUID');
                  }
                }
              })
            );
          }
        } catch (err) {
          this.logger.error(err, 'recalculateVatRates:takeAway:DbDaoService:getDataByUUID');
        }
      }
    }

    activeInvoice.totalAmountWithoutTaxes =
      activeInvoice.totalAmountWithoutDiscount - activeInvoice.invoiceEntries.reduce((acc, cur) => acc + cur['totalTaxAmount'], 0);
    activeInvoice.totalAmountWithoutTaxes = parseFloat(activeInvoice.totalAmountWithoutTaxes.toFixed(2));
    this.activeInvoice = activeInvoice;
    this.saveInvoice();
  }

  invoiceEntrySwipe(swipeEvent: InvoiceDetailsSwipeEvent<InvoiceEntry>) {
    const { direction, entity } = swipeEvent;
    if (direction === SwipeDirection.BACK) {
      this.changeEntryQuantity(entity, -1);
    } else if (direction === SwipeDirection.FORWARD) {
      if (entity?.type === PRODUCT_TYPES.GIFT_CARD || entity?.type === PRODUCT_TYPES.TIPS) {
        return;
      }
      this.changeEntryQuantity(entity, 1);
    }
  }

  checkPartialPaymentInInvoice(message: string = 'invoice_has_partial_payment_error'): boolean {
    const hasPayments = this.activeInvoice?.isInvoiceHasPayments();
    const isDisableAction = hasPayments && !this.activeInvoice?.isPaid && !this.activeInvoice?.isWebshop;
    if (isDisableAction) {
      this.userNotificationService.showMessage(message);
    }
    return isDisableAction;
  }

  public addNewGuest(saveRequired: boolean = true) {
    const newInvoiceEntryGuest = this.multipleGuestsService.createNewGuest();
    this.addEntry(newInvoiceEntryGuest, PRODUCT_TYPES.CATEGORY, 1, saveRequired);
  }

  public async addFirstGuestToSelfOrderInvoice(saveRequired: boolean = true): Promise<void> {
    const newInvoiceEntryGuest = this.multipleGuestsService.createNewGuest();

    if (Boolean(this.activeInvoice?.customer?.uuid)) {
      const customer: Customer = await this.customerService.getCustomerByUuid(this.activeInvoice.customer.uuid);
      newInvoiceEntryGuest.name = `${customer.firstName} ${customer.lastName}`;
      newInvoiceEntryGuest.customerIdentifier = customer.uuid;
    }

    this.addEntry(newInvoiceEntryGuest, PRODUCT_TYPES.CATEGORY, 1, false);
    this.recalculatePositionForFirstGuestInSelfOrderInvoice();
  }

  public addCustomerToGuest(customer: Customer, guestNumber: number, invoice?: Invoice): Invoice {
    invoice = invoice || this.activeInvoice;
    const invoiceEntryGuest = invoice.getGuestInvoiceEntryByGuestNumber(guestNumber);
    if (!invoiceEntryGuest) {
      return;
    }
    this.multipleGuestsService.addCustomerToGuest(invoiceEntryGuest, customer, guestNumber);
    return invoice;
  }

  private addActiveEmployeeToSelfOrderInvocie(): void {
    if (!!this.activeInvoice && this.activeInvoice?.subChannel === SalesSubChannelTypesEnum.SELFORDER && !this.activeInvoice?.employee) {
      const employee: Employee = this.employeeFacadeStore?.activeEmployeeSnapshot;
      if (employee && !employee?.deleted && employee?.uuid) {
        this.activeInvoice.employee = {
          uuid: employee.uuid,
        };
      }
    }
  }

  private recalculatePositionForFirstGuestInSelfOrderInvoice(): void {
    const invoiceEntries: InvoiceEntry[] = [...this.activeInvoice.invoiceEntries];

    const index: number = invoiceEntries.findIndex((item: InvoiceEntry): boolean => !!item.isGuest);

    if (index <= 0) {
      return;
    }

    const [itemWithTrue] = invoiceEntries.splice(index, 1);
    invoiceEntries.unshift(itemWithTrue);

    invoiceEntries.forEach((item: InvoiceEntry, index: number) => {
      item.position = index;
    });

    this.activeInvoice.invoiceEntries = [...invoiceEntries];

    this.InvoicesService.saveInvoice(this.activeInvoice).then();
  }
}
